import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	createMIPContract,
	deleteMIPContract,
	getDocumentWorkflowDraftTemplates,
	updateMIPContract,
	uploadFile,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	useTranslationCommonUi,
	useTranslationDefault,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { webIsProduction } from '@dealstack/frontend/shared/environment';
import {
	ContractType,
	CreateContractDto,
	DataContractTct,
	DataCounterpartyTct,
	DocumentWorkflowTemplateVisibleIn,
	SignatureType,
	InvestmentWorkflowType,
} from '@dealstack/shared/lib/generated';
import { isReviewSignatureType } from '@dealstack/shared/lib/common/signatures';
import {
	hasContractChangedFromSaved,
	shouldResetReviewSignatureForForm,
} from './useContractsController.utils';
import { fileNameToDocumentName } from '@dealstack/shared/lib/common/file/file';
import { showPreviewDocumentFromStream } from '@dealstack/frontend/shared/documentPreview';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Path, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useDownloadedFileBlobMutation } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlobMutation';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

export type ContractDataItem = Omit<
	DataContractTct,
	'mipId' | 'createdBy' | 'esignProvider' | 'archived'
> & {
	local?: boolean;
	counterparties: Array<DataCounterpartyTct>;
	hasWitnesses?: boolean;
	applicability?: InvestmentWorkflowType[];
};
type ContractDataFormValues = {
	contracts: ContractDataItem[] | null;
};
export const useContractsController = (visibleIn?: DocumentWorkflowTemplateVisibleIn[]) => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const [remoteContracts, setRemoteContracts] = useState<DataContractTct[] | null>(null);

	const tMipCreate = useTranslationMipCreate();
	const tDefault = useTranslationDefault();
	const tCommonUi = useTranslationCommonUi();

	const mipCreateController = useContext(MIPCreateContext);

	const contractsBackupData = mipCreateController?.dataBackup?.[MIP_CREATE_ROUTES.CONTRACTS];

	const commonYup = useCommonYupValidation();
	const validationSchema = useMemo(() => {
		return Yup.object().shape({
			contracts: Yup.array().of(
				Yup.object().shape({
					name: commonYup.contract_name.required,
				})
			),
		});
	}, []);

	const contractsMainForm = useForm<ContractDataFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			contracts: [],
		},
		resolver: yupResolver(validationSchema),
	});

	const { reset } = contractsMainForm;

	const contractFormWatch = contractsMainForm.watch();

	const {
		fields: contractFields,
		append,
		remove,
		update,
	} = useFieldArray({
		name: 'contracts',
		control: contractsMainForm.control,
		// we use keyName to prevent override backend contract id by rhf in fields
		keyName: 'key' as 'id',
	});

	const selectedFieldsToWatch = useMemo(() => {
		if (!contractFormWatch.contracts) {
			return [];
		}

		const fields: Path<ContractDataFormValues>[] = [];

		for (let i = 0; i < contractFormWatch.contracts.length; i++) {
			fields.push(`contracts.${i}.type`, `contracts.${i}.signatureType`);
		}

		return fields;
	}, [contractFormWatch]);

	const contractSelectedFieldsFormWatch = contractsMainForm.watch(selectedFieldsToWatch);

	useEffect(() => {
		if (!contractFormWatch.contracts) {
			return;
		}

		for (const [index, contract] of contractFormWatch.contracts.entries()) {
			if (
				(contract.type === ContractType.FORM ||
					contract.signatureType === SignatureType.REVIEW) &&
				contract.hasWitnesses
			) {
				contractsMainForm.setValue(`contracts.${index}.hasWitnesses`, false);
			}

			if (shouldResetReviewSignatureForForm(contract.type, contract.signatureType)) {
				contractsMainForm.setValue(
					`contracts.${index}.signatureType`,
					SignatureType.ELECTRONIC
				);
			}
		}
	}, [...contractSelectedFieldsFormWatch, contractFormWatch.contracts]);

	const getContractsFn = useCallback(
		async (_args?: void, options?: Parameters<typeof getDocumentWorkflowDraftTemplates>[1]) => {
			return getDocumentWorkflowDraftTemplates(
				{
					dealId,
					programmeId: mipId,
					visibleIn,
				},
				options
			);
		},
		[dealId, mipId]
	);
	const getContractsRequest = useWrappedRequest(getContractsFn);
	const createContractRequest = useWrappedRequest(createMIPContract);
	const updateContractRequest = useWrappedRequest(updateMIPContract);
	const deleteContractRequest = useWrappedRequest(deleteMIPContract);
	const { downloadedFileBlob } = useDownloadedFileBlobMutation();
	const uploadFileRequest = useWrappedRequest(uploadFile);

	useEffect(() => {
		if (contractsBackupData) {
			setRemoteContracts(contractsBackupData.remoteContracts ?? []);
			contractsMainForm.reset({ contracts: contractsBackupData.contracts ?? [] });
			return;
		}
		void getContracts();
	}, [contractsBackupData]);

	const getContracts = async () => {
		let fetchedContracts: DataContractTct[] | null = null;
		await getContractsRequest
			.fetch()
			.onSuccess((success) => {
				fetchedContracts = success?.payload ?? [];
				setRemoteContracts(fetchedContracts);
			})
			.concludeWithPromise();
		return fetchedContracts;
	};

	const showPreviewDocument = async (id: string) => {
		const contractItem = contractFields.find((item) => item.id === id);

		if (!contractItem) {
			return;
		}
		const data = await downloadedFileBlob({ fileId: contractItem.file.id, dealId });

		await showPreviewDocumentFromStream(data.blob);
	};
	useEffect(() => {
		if (remoteContracts) {
			const contractArray = remoteContracts.map((item) => {
				const formItem: ContractDataItem = {
					file: item.file,
					name: item.name,
					type: item.type ?? ContractType.DEAL,
					isExpired: item.isExpired,
					isRejected: item.isRejected,
					requiredWitnesses: item.requiredWitnesses,
					counterparties: item.counterparties?.map((cpItem) => {
						return { ...cpItem, remoteId: cpItem.id };
					}),
					instruction: item.instruction,
					signatureType: item.signatureType ?? SignatureType.ELECTRONIC,
					id: item.id,
					local: false,
					hasWitnesses: !!item.requiredWitnesses,
					applicability: item.applicability || [
						InvestmentWorkflowType.JOINER_CREATE,
						InvestmentWorkflowType.JOINER,
						InvestmentWorkflowType.ALLOCATION,
					],
					visibleIn: [DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_BUYING],
				};
				return formItem;
			});
			contractsMainForm.clearErrors();
			contractsMainForm.reset({ contracts: contractArray });
			contractArray.forEach((contract, index) => {
				update(index, contract);
			});
		}
	}, [remoteContracts]);

	useEffect(() => {
		const file = uploadFileRequest.response?.payload;
		if (file?.id) {
			append({
				file,
				name: fileNameToDocumentName(file.name),
				instruction: '',
				isExpired: false,
				isRejected: false,
				counterparties: [],
				requiredWitnesses: 0,
				id: generateUUID(),
				signatureType: webIsProduction ? SignatureType.ELECTRONIC : SignatureType.WET_INK,
				type: ContractType.DEAL,
				local: true,
				hasWitnesses: false,
				applicability: [
					InvestmentWorkflowType.JOINER_CREATE,
					InvestmentWorkflowType.JOINER,
					InvestmentWorkflowType.ALLOCATION,
				],
				visibleIn: [DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_BUYING],
			});
		}
	}, [uploadFileRequest.response?.payload]);

	const saveContracts = async (): Promise<{
		result: WizardSaveResult;
		contracts: DataContractTct[] | null;
	}> => {
		const allContracts = contractsMainForm.getValues('contracts') ?? [];

		const contracts = allContracts.map((contract) => ({
			...contract,
			requiredWitnesses: contract.hasWitnesses ? 1 : 0,
		}));

		mipCreateController?.setIsLoading(true);
		let isAnyFailed = false;
		let isLocalError = false;

		const toUpdate = extractContractsToUpdate(contracts);

		const updateContractPromise = (() => {
			return toUpdate?.map((item) => {
				const counterpartiesBody = isReviewSignatureType(item.signatureType)
					? []
					: item.counterparties;
				return updateContractRequest
					.fetch({
						name: item.name,
						type: item.type,
						signatureType: item.signatureType,
						file: item.file,
						requiredWitnesses: item.requiredWitnesses,
						counterparties: counterpartiesBody,
						instruction: item.instruction,
						applicability: item.applicability,
						id: item.id,
					} as CreateContractDto & { id: string })
					.onAnyError(() => {
						isAnyFailed = true;
					})
					.concludeWithPromise();
			});
		})();

		const toCreate = extractContractsToCreate(contracts);
		const createContractPromise = (() => {
			return toCreate?.map((item) => {
				const counterpartiesBody = isReviewSignatureType(item.signatureType)
					? []
					: item.counterparties;
				return createContractRequest
					.fetch({
						name: item.name,
						type: item.type,
						signatureType: item.signatureType,
						file: item.file,
						requiredWitnesses: item.requiredWitnesses,
						counterparties: counterpartiesBody,
						instruction: item.instruction,
						applicability: item.applicability,
					} as CreateContractDto)
					.onAnyError(() => {
						isAnyFailed = true;
					})
					.concludeWithPromise();
			});
		})();

		const toDelete = extractContractsToDelete(contracts);
		const deleteContractPromise = (() => {
			return (
				toDelete?.map((item) => {
					return deleteContractRequest
						.fetch({
							id: item.id,
						})
						.onLocalError((error) => {
							if (isStandardBackendError(error)) {
								showToastV2({
									title: tDefault(`error_codes.${error.error?.code}`),
									state: 'error',
								});
								isLocalError = true;
							}
						})
						.onAnyError(() => {
							isAnyFailed = true;
						})
						.concludeWithPromise();
				}) ?? []
			);
		})();

		await Promise.allSettled([
			...updateContractPromise,
			...createContractPromise,
			...deleteContractPromise,
		]);
		mipCreateController?.setIsLoading(false);
		if (isAnyFailed) {
			mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.CONTRACTS, {
				contracts: contractsMainForm.getValues('contracts'),
			});
			if (isLocalError) {
				return { result: SAVE_FAILED, contracts: null };
			}
			showToastV2({
				title: tMipCreate('contracts.table.errors.save_contracts_fail'),
				state: 'error',
			});
			return { result: SAVE_FAILED, contracts: null };
		}
		mipCreateController?.removeBackupData(MIP_CREATE_ROUTES.CONTRACTS);
		reset({}, { keepValues: true });
		return { result: SAVE_OK, contracts: await getContracts() };
	};
	const sendRequests = async () => (await saveContracts()).result;
	const extractContractsToUpdate = (array: ContractDataItem[]) => {
		return array?.filter((localItem) => {
			if (localItem.name === '') {
				return;
			}
			const item = remoteContracts?.find((remoteItem) => remoteItem.id === localItem.id);
			if (!item) {
				return false;
			}
			// applicability was previously compared against itself on both sides (a no-op),
			// so leaving it out of the shared comparison keeps behaviour identical.
			return hasContractChangedFromSaved(localItem, item);
		});
	};
	const extractContractsToCreate = (array: ContractDataItem[]) => {
		const filteredArray = array
			?.filter((localItem) => localItem.local === true)
			.filter((contract) => contract.name !== '');
		return filteredArray;
	};
	const extractContractsToDelete = (array: ContractDataItem[]) => {
		const filteredArray = remoteContracts?.filter(
			(remoteItem) => !array?.some((item) => remoteItem.id === item.id)
		);
		return filteredArray;
	};
	useWizardStep(sendRequests);

	const onRemoveRowClicked = (rowID: string) => {
		const formContracts = contractsMainForm.getValues('contracts');
		if (formContracts) {
			const indexToRemove = contractFields.findIndex((item) => item.id === rowID);
			if (indexToRemove < 0) {
				return;
			}
			remove(indexToRemove);
		}
	};

	useEffect(() => {
		const formContracts = contractsMainForm.getValues('contracts');
		const shouldDisableContinue =
			formContracts?.some((contract) => contract.name.trim() === '') ?? false;
		mipCreateController?.setDisableContinueButton(shouldDisableContinue);
		mipCreateController?.setPopoverMessage(
			tCommonUi('contracts.signed.continue_disabled_info')
		);
	}, [contractsMainForm.getValues()]);

	// Unmount only: the effect above re-runs every render, so clearing the flag there would
	// fight the line that sets it.
	useEffect(
		() => () => {
			mipCreateController?.setDisableContinueButton(false);
		},
		[]
	);

	return {
		uploadFileRequest,
		contractsMainForm,
		onRemoveRowClicked,
		contractFields,
		updateFields: update,
		showPreviewDocument,
		saveContractsDraft: async () => (await saveContracts()).contracts,
		remoteContracts,
	};
};
